<template>
    <div v-if="groups.length" class="references">
        <button
            type="button"
            class="references-toggle"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
        >
            <span>
                {{ $t('patty.auditReferences.title') }}
                <span class="reference-count">({{ referenceCount }})</span>
            </span>
            <i
                :class="expanded ? 'fi fi-chevron-up' : 'fi fi-chevron-down'"
                aria-hidden="true"
            ></i>
        </button>

        <div v-if="expanded" class="reference-groups">
            <section
                v-for="group in groups"
                :key="group.type"
                class="reference-group"
            >
                <h4 class="group-title">
                    {{ groupLabel(group.type) }}
                    <span class="reference-count">
                        ({{ group.references.length }})
                    </span>
                </h4>
                <div class="reference-list">
                    <button
                        v-for="reference in group.references"
                        :key="`${reference.type}:${reference.hash}`"
                        type="button"
                        class="reference-button"
                        @click="$emit('open-reference', reference)"
                    >
                        <i :class="icon(reference.type)" aria-hidden="true"></i>
                        <span class="reference-copy">
                            {{
                                reference.label || fallbackLabel(reference.type)
                            }}
                        </span>
                        <i class="fi fi-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { groupAuditReferences } from './auditAgent.js';

export default {
    name: 'PattyAuditReferences',
    props: { references: { type: Array, default: () => [] } },
    emits: ['open-reference'],
    data() {
        return { expanded: false };
    },
    computed: {
        groups() {
            return groupAuditReferences(this.references);
        },
        referenceCount() {
            return this.groups.reduce(
                (total, group) => total + group.references.length,
                0
            );
        },
    },
    methods: {
        groupLabel(type) {
            return this.$t(
                {
                    AUDIT_RECORD: 'patty.auditReferences.groups.audits',
                    AUDIT_GAP: 'patty.auditReferences.groups.gaps',
                    ACTION_POINT: 'patty.auditReferences.groups.actionPoints',
                }[type]
            );
        },
        fallbackLabel(type) {
            return this.$t(
                {
                    AUDIT_RECORD: 'patty.auditReferences.types.audit',
                    AUDIT_GAP: 'patty.auditReferences.types.gap',
                    ACTION_POINT: 'patty.auditReferences.types.actionPoint',
                }[type]
            );
        },
        icon(type) {
            return {
                AUDIT_RECORD: 'fi fi-audit title-icon',
                AUDIT_GAP: 'fi fi-alert-circle',
                ACTION_POINT: 'fi fi-clipboard',
            }[type];
        },
    },
};
</script>

<style scoped>
.references {
    overflow: hidden;
    border: 1px solid var(--neutral-500);
    border-radius: var(--radius-default);
    background: var(--white);
}

.references-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: var(--space-2);
    padding: var(--space-3);
    border: 0;
    background: transparent;
    color: var(--neutral-800);
    font-size: 0.8125rem;
    font-weight: 700;
    text-align: left;
}

.reference-count {
    color: var(--neutral-700);
    font-weight: 400;
}

.reference-groups {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: 0 var(--space-3) var(--space-3);
}

.reference-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

.group-title {
    margin: 0;
    color: var(--neutral-700);
    font-size: 0.75rem;
}

.reference-list {
    overflow: hidden;
    border: 1px solid var(--neutral-500);
    border-radius: var(--radius-default);
}

.reference-button {
    display: grid;
    grid-template-columns: 2.25rem minmax(0, 1fr) auto;
    align-items: center;
    width: 100%;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border: 0;
    border-bottom: 1px solid var(--neutral-500);
    background: var(--white);
    color: var(--neutral-900);
    text-align: left;
}

.reference-button:last-child {
    border-bottom: 0;
}

.reference-button:hover {
    background: var(--neutral-450);
}

.reference-button > i:first-child:not(.title-icon) {
    justify-self: center;
    color: var(--primary-500);
    font-size: 1.25rem;
}

.reference-copy {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8125rem;
    font-weight: 700;
}
</style>
